// pages/TiposRegimen.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Divider,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BlockOutlined,
  UnlockOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  ExportOutlined,
  RestOutlined,
  CoffeeOutlined,
  ShoppingOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;

const TiposRegimen = () => {
  // Estados
  const [tiposRegimen, setTiposRegimen] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [globalSearchText, setGlobalSearchText] = useState('');
  const [seriesEntrada, setSeriesEntrada] = useState([]);
  const [seriesSalida, setSeriesSalida] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentRegimen, setCurrentRegimen] = useState(null);
  const [form] = Form.useForm();
  const [tableLoading, setTableLoading] = useState(false);
  
  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Cargar datos iniciales
  useEffect(() => {
    fetchTiposRegimen();
    fetchSeriesEntrada();
    fetchSeriesSalida();
  }, []);

  const fetchTiposRegimen = async () => {
    setTableLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/tiposregimen`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Transformar datos booleanos si vienen como 0/1
      const transformedData = response.data.data.map(item => ({
        ...item,
        Activo: item.Activo === true || item.Activo === 1 || item.Activo === '1',
        TieneDesayuno: item.TieneDesayuno === true || item.TieneDesayuno === 1 || item.TieneDesayuno === '1',
        TieneAlmuerzo: item.TieneAlmuerzo === true || item.TieneAlmuerzo === 1 || item.TieneAlmuerzo === '1',
        TieneCena: item.TieneCena === true || item.TieneCena === 1 || item.TieneCena === '1'
      }));
      
      setTiposRegimen(transformedData);
      setFilteredData(transformedData);
      
      console.log('✅ Datos cargados:', transformedData);
    } catch (error) {
      message.error('Error al cargar los tipos de régimen');
      console.error('Error fetchTiposRegimen:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchSeriesEntrada = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/tiposregimen/series-entrada`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeriesEntrada(response.data.data);
    } catch (error) {
      console.error('Error cargando series de entrada:', error);
    }
  };

  const fetchSeriesSalida = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/tiposregimen/series-salida`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeriesSalida(response.data.data);
    } catch (error) {
      console.error('Error cargando series de salida:', error);
    }
  };

  // Función para búsqueda global
  const handleGlobalSearch = (searchText) => {
    setGlobalSearchText(searchText);
    
    if (!searchText.trim()) {
      // Si no hay texto, mostrar todos los registros
      setFilteredData(tiposRegimen);
      return;
    }
    
    const lowerSearchText = searchText.toLowerCase().trim();
    
    // Buscar en todos los campos de cada registro
    const filtered = tiposRegimen.filter(record => {
      // Buscar en todos los campos del registro
      return Object.keys(record).some(key => {
        const value = record[key];
        
        // Ignorar campos undefined, null o que no sean strings/números
        if (value === null || value === undefined) return false;
        
        // Convertir a string y buscar
        const stringValue = String(value).toLowerCase();
        return stringValue.includes(lowerSearchText);
      });
    });
    
    setFilteredData(filtered);
  };

  // Configuración de columnas
  const columns = [
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
      width: 80,
      sorter: (a, b) => a.Codigo.localeCompare(b.Codigo),
      sortOrder: sortedInfo.columnKey === 'Codigo' && sortedInfo.order,
    },
    {
      title: 'Descripción',
      dataIndex: 'Descripcion',
      key: 'Descripcion',
      width: 200,
      sorter: (a, b) => a.Descripcion.localeCompare(b.Descripcion),
      sortOrder: sortedInfo.columnKey === 'Descripcion' && sortedInfo.order,
      ellipsis: true,
    },
    {
      title: 'Desc. Corta',
      dataIndex: 'DescripcionCorta',
      key: 'DescripcionCorta',
      width: 100,
      sorter: (a, b) => (a.DescripcionCorta || '').localeCompare(b.DescripcionCorta || ''),
      sortOrder: sortedInfo.columnKey === 'DescripcionCorta' && sortedInfo.order,
    },
    {
      title: 'Ser. Entrada',
      dataIndex: 'SerEntrada',
      key: 'SerEntrada',
      width: 100,
      sorter: (a, b) => (a.SerEntrada || '').localeCompare(b.SerEntrada || ''),
      sortOrder: sortedInfo.columnKey === 'SerEntrada' && sortedInfo.order,
      filters: seriesEntrada.map(serie => ({
        text: serie,
        value: serie
      })),
      filteredValue: filteredInfo.SerEntrada || null,
      onFilter: (value, record) => record.SerEntrada === value,
    },
    {
      title: 'Ser. Salida',
      dataIndex: 'SerSalida',
      key: 'SerSalida',
      width: 100,
      sorter: (a, b) => (a.SerSalida || '').localeCompare(b.SerSalida || ''),
      sortOrder: sortedInfo.columnKey === 'SerSalida' && sortedInfo.order,
      filters: seriesSalida.map(serie => ({
        text: serie,
        value: serie
      })),
      filteredValue: filteredInfo.SerSalida || null,
      onFilter: (value, record) => record.SerSalida === value,
    },
    {
      title: 'Orden',
      dataIndex: 'Orden',
      key: 'Orden',
      width: 80,
      sorter: (a, b) => (a.Orden || 9999) - (b.Orden || 9999),
      sortOrder: sortedInfo.columnKey === 'Orden' && sortedInfo.order,
      render: (orden) => orden ? orden : '-',
    },
    {
      title: 'Estado',
      dataIndex: 'Activo',
      key: 'Activo',
      width: 100,
      render: (activo) => (
        <Badge
          status={activo ? 'success' : 'error'}
          text={activo ? 'Activo' : 'Inactivo'}
        />
      ),
      filters: [
        { text: 'Activos', value: true },
        { text: 'Inactivos', value: false },
      ],
      filteredValue: filteredInfo.Activo || null,
      onFilter: (value, record) => record.Activo === value,
    },
    {
      title: 'Desayuno',
      dataIndex: 'TieneDesayuno',
      key: 'TieneDesayuno',
      width: 100,
      render: (tiene) => (
        <Tag color={tiene ? 'green' : 'default'}>
          {tiene ? 'SÍ' : 'NO'}
        </Tag>
      ),
    },
    {
      title: 'Almuerzo',
      dataIndex: 'TieneAlmuerzo',
      key: 'TieneAlmuerzo',
      width: 100,
      render: (tiene) => (
        <Tag color={tiene ? 'green' : 'default'}>
          {tiene ? 'SÍ' : 'NO'}
        </Tag>
      ),
    },
    {
      title: 'Cena',
      dataIndex: 'TieneCena',
      key: 'TieneCena',
      width: 100,
      render: (tiene) => (
        <Tag color={tiene ? 'green' : 'default'}>
          {tiene ? 'SÍ' : 'NO'}
        </Tag>
      ),
    },
    {
      title: 'Servicios',
      key: 'servicios',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Des: {record.ServicioDesayuno || 0}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Alm: {record.ServicioAlmuerzo || 0}
          </Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Cen: {record.ServicioCena || 0}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Tooltip title={record.Activo ? "Desactivar" : "Activar"}>
            <Button
              type={record.Activo ? "dashed" : "primary"}
              danger={record.Activo}
              icon={record.Activo ? <BlockOutlined /> : <UnlockOutlined />}
              size="small"
              onClick={() => handleToggleActive(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar tipo de régimen?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.Codigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
          
          <Tooltip title="Ver detalle">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Handlers para las acciones
  const handleCreate = () => {
    setModalType('create');
    setCurrentRegimen(null);
    form.resetFields();
    form.setFieldsValue({
      Activo: true,
      TieneDesayuno: false,
      TieneAlmuerzo: false,
      TieneCena: false,
      ServicioDesayuno: 0,
      ServicioAlmuerzo: 0,
      ServicioCena: 0,
      Orden: 0
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentRegimen(record);

    console.log("📝 Registro para editar:", record);
    
    form.setFieldsValue({
      ...record,
      Activo: record.Activo || false,
      TieneDesayuno: record.TieneDesayuno || false,
      TieneAlmuerzo: record.TieneAlmuerzo || false,
      TieneCena: record.TieneCena || false,
      ServicioDesayuno: record.ServicioDesayuno || 0,
      ServicioAlmuerzo: record.ServicioAlmuerzo || 0,
      ServicioCena: record.ServicioCena || 0,
      Orden: record.Orden || 0
    });
    setModalVisible(true);
  };

  const handleView = (record) => {
    Modal.info({
      title: `Detalle de Régimen: ${record.Descripcion}`,
      width: 700,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <strong>Código:</strong> {record.Codigo}
            </Col>
            <Col span={18}>
              <strong>Descripción:</strong> {record.Descripcion}
            </Col>
            <Col span={6}>
              <strong>Desc. Corta:</strong> {record.DescripcionCorta || '-'}
            </Col>
            <Col span={6}>
              <strong>Orden:</strong> {record.Orden || '-'}
            </Col>
            <Col span={6}>
              <strong>Ser. Entrada:</strong> {record.SerEntrada || '-'}
            </Col>
            <Col span={6}>
              <strong>Ser. Salida:</strong> {record.SerSalida || '-'}
            </Col>
            <Col span={6}>
              <strong>Estado:</strong> {record.Activo ? 'ACTIVO' : 'INACTIVO'}
            </Col>
            <Col span={6}>
              <strong>Incluye Desayuno:</strong> {record.TieneDesayuno ? 'SÍ' : 'NO'}
            </Col>
            <Col span={6}>
              <strong>Incluye Almuerzo:</strong> {record.TieneAlmuerzo ? 'SÍ' : 'NO'}
            </Col>
            <Col span={6}>
              <strong>Incluye Cena:</strong> {record.TieneCena ? 'SÍ' : 'NO'}
            </Col>
            <Col span={24}>
              <Divider orientation="left">Servicios</Divider>
            </Col>
            <Col span={8}>
              <strong>Serv. Desayuno:</strong> {record.ServicioDesayuno || 0}
            </Col>
            <Col span={8}>
              <strong>Serv. Almuerzo:</strong> {record.ServicioAlmuerzo || 0}
            </Col>
            <Col span={8}>
              <strong>Serv. Cena:</strong> {record.ServicioCena || 0}
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleToggleActive = async (record) => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem('token');
      const nuevoEstado = !record.Activo;
      
      const response = await axios.patch(
        `${BASE_URL}/api/tiposregimen/${record.Codigo}/estado`,
        { Activo: nuevoEstado },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Actualizar el estado local con el dato devuelto por el backend
      const regimenActualizado = response.data.data;
      const regimenActualizadoTransformado = {
        ...regimenActualizado,
        Activo: regimenActualizado.Activo === true || regimenActualizado.Activo === 1 || regimenActualizado.Activo === '1'
      };
      
      // Actualizar el array de tiposRegimen
      setTiposRegimen(tiposRegimen.map(tr => 
        tr.Codigo === record.Codigo ? regimenActualizadoTransformado : tr
      ));
      
      // También actualizar filteredData si aplica
      setFilteredData(prev => prev.map(tr => 
        tr.Codigo === record.Codigo ? regimenActualizadoTransformado : tr
      ));
      
      message.success(`Tipo de régimen ${nuevoEstado ? 'activado' : 'desactivado'} correctamente`);
      
    } catch (error) {
      message.error('Error al actualizar el estado');
      console.error('Error handleToggleActive:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleDelete = async (codigo) => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${BASE_URL}/api/tiposregimen/${codigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remover del estado local
      const nuevosTiposRegimen = tiposRegimen.filter(tr => tr.Codigo !== codigo);
      setTiposRegimen(nuevosTiposRegimen);
      
      // También actualizar filteredData
      const nuevoFilteredData = filteredData.filter(tr => tr.Codigo !== codigo);
      setFilteredData(nuevoFilteredData);
      
      message.success('Tipo de régimen eliminado correctamente');
      
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar el tipo de régimen');
      console.error('Error handleDelete:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      
      console.log('📝 Datos a guardar:', values);
      
      if (modalType === 'create') {
        const response = await axios.post(
          `${BASE_URL}/api/tiposregimen`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Transformar datos booleanos
        const nuevoRegimen = {
          ...response.data.data,
          Activo: response.data.data.Activo === true || response.data.data.Activo === 1 || response.data.data.Activo === '1',
          TieneDesayuno: response.data.data.TieneDesayuno === true || response.data.data.TieneDesayuno === 1 || response.data.data.TieneDesayuno === '1',
          TieneAlmuerzo: response.data.data.TieneAlmuerzo === true || response.data.data.TieneAlmuerzo === 1 || response.data.data.TieneAlmuerzo === '1',
          TieneCena: response.data.data.TieneCena === true || response.data.data.TieneCena === 1 || response.data.data.TieneCena === '1'
        };
        
        // Agregar al estado local
        setTiposRegimen([...tiposRegimen, nuevoRegimen]);
        
        // Si hay búsqueda activa, actualizar filteredData también
        if (globalSearchText) {
          const lowerSearchText = globalSearchText.toLowerCase();
          const shouldShowInSearch = Object.keys(nuevoRegimen).some(key => {
            const value = nuevoRegimen[key];
            if (value === null || value === undefined) return false;
            return String(value).toLowerCase().includes(lowerSearchText);
          });
          
          if (shouldShowInSearch) {
            setFilteredData([...filteredData, nuevoRegimen]);
          }
        } else {
          setFilteredData([...filteredData, nuevoRegimen]);
        }
        
        message.success('Tipo de régimen creado correctamente');
        
      } else {
        // Edición
        console.log("📝 Editando régimen:", currentRegimen.Codigo);
        console.log("📝 Valores:", values);
        
        const response = await axios.put(
          `${BASE_URL}/api/tiposregimen/${currentRegimen.Codigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Transformar datos booleanos
        const regimenActualizado = {
          ...response.data.data,
          Activo: response.data.data.Activo === true || response.data.data.Activo === 1 || response.data.data.Activo === '1',
          TieneDesayuno: response.data.data.TieneDesayuno === true || response.data.data.TieneDesayuno === 1 || response.data.data.TieneDesayuno === '1',
          TieneAlmuerzo: response.data.data.TieneAlmuerzo === true || response.data.data.TieneAlmuerzo === 1 || response.data.data.TieneAlmuerzo === '1',
          TieneCena: response.data.data.TieneCena === true || response.data.data.TieneCena === 1 || response.data.data.TieneCena === '1'
        };
        
        // Actualizar el estado local
        const nuevosTiposRegimen = tiposRegimen.map(tr => 
          tr.Codigo === currentRegimen.Codigo ? regimenActualizado : tr
        );
        
        setTiposRegimen(nuevosTiposRegimen);
        
        // Actualizar filteredData
        const nuevoFilteredData = filteredData.map(tr => 
          tr.Codigo === currentRegimen.Codigo ? regimenActualizado : tr
        );
        
        setFilteredData(nuevoFilteredData);
        
        message.success('Tipo de régimen actualizado correctamente');
      }
      
      // Cerrar modal y limpiar formulario
      setModalVisible(false);
      form.resetFields();
      
    } catch (error) {
      console.error('❌ Error en handleSave:', error);
      console.error('❌ Response:', error.response);
      
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error.response?.data?.details) {
        message.error(error.response.data.details);
      } else {
        message.error('Error al guardar los datos');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (pagination, filters, sorter) => {
    console.log('Filtros aplicados:', filters);
    
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    
    // Aplicar filtros de columnas
    let result = [...tiposRegimen];
    
    // Aplicar filtros de Ant Design
    Object.keys(filters).forEach(key => {
      const filterValues = filters[key];
      if (filterValues && filterValues.length > 0) {
        result = result.filter(item => {
          const itemValue = item[key];
          
          if (key === 'Activo') {
            return filterValues.includes(itemValue);
          }
          
          if (key === 'SerEntrada' || key === 'SerSalida') {
            return filterValues.includes(itemValue);
          }
          
          return true;
        });
      }
    });
    
    // Aplicar ordenación
    if (sorter.field && sorter.order) {
      result.sort((a, b) => {
        const aVal = a[sorter.field] || '';
        const bVal = b[sorter.field] || '';
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          if (sorter.order === 'ascend') {
            return aVal.localeCompare(bVal);
          } else {
            return bVal.localeCompare(aVal);
          }
        } else {
          if (sorter.order === 'ascend') {
            return (aVal || 0) - (bVal || 0);
          } else {
            return (bVal || 0) - (aVal || 0);
          }
        }
      });
    }
    
    // Aplicar búsqueda global si existe
    if (globalSearchText) {
      const lowerSearchText = globalSearchText.toLowerCase().trim();
      result = result.filter(record => {
        return Object.keys(record).some(key => {
          const value = record[key];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(lowerSearchText);
        });
      });
    }
    
    setFilteredData(result);
  };

  const clearFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setGlobalSearchText('');
    setFilteredData(tiposRegimen);
    message.success('Filtros y búsqueda limpiados');
  };

  // Función para exportar
  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Regímenes" 
          value={tiposRegimen.length}
          prefix={<RestOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Activos" 
          value={tiposRegimen.filter(tr => tr.Activo).length}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Con Desayuno" 
          value={tiposRegimen.filter(tr => tr.TieneDesayuno).length}
          valueStyle={{ color: '#faad14' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Pensión Completa" 
          value={tiposRegimen.filter(tr => tr.TieneDesayuno && tr.TieneAlmuerzo && tr.TieneCena).length}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
    </Row>
  );

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input.Search
          placeholder="Buscar por código, descripción, serie, etc...."
          value={globalSearchText}
          style={{ width: 400 }}
          allowClear
          enterButton
          onChange={(e) => {
            setGlobalSearchText(e.target.value);
            // Si el usuario borra todo el texto, buscar inmediatamente
            if (e.target.value === '') {
              handleGlobalSearch('');
            }
          }}
          onSearch={handleGlobalSearch}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={clearFilters}
          size="small"
        >
          Limpiar
        </Button>
        <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
          <strong>{filteredData.length}</strong> registros
        </Tag>
      </Space>
    </div>
  );

  return (
    <>
      <PageContainer
        title="Gestión de Tipos de Régimen"
        subtitle={`${tiposRegimen.length} tipos de régimen registrados`}
        icon={<RestOutlined />}
        loading={tableLoading}
        onRefresh={fetchTiposRegimen}
        onCreate={handleCreate}
        onExport={handleExport}
        badgeCount={tiposRegimen.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{tiposRegimen.length}</strong> registros
            </Tag>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}

        {searchBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="Codigo"
          loading={tableLoading}
          onChange={handleChange}
          scroll={{ x: 1500 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar */}
      <Modal
        title={modalType === 'create' ? 'Nuevo Tipo de Régimen' : 'Editar Tipo de Régimen'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={800}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="Codigo"
                label="Código"
                rules={[{ required: true, message: 'Por favor ingrese el código' },
                    { max: 2, message: 'El código debe tener máximo 2 caracteres' }
                ]}
              >
                <Input 
                  placeholder="Ej: PC" 
                  disabled={modalType === 'edit'} 
                  maxLength={2}
                  showCount 
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="Descripcion"
                label="Descripción"
                rules={[{ required: true, message: 'Por favor ingrese la descripción' }]}
              >
                <Input placeholder="Descripción completa del régimen" maxLength={100} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="DescripcionCorta"
                label="Descripción Corta"
              >
                <Input placeholder="Ej: P.C." maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="Orden"
                label="Orden"
              >
                <InputNumber 
                  placeholder="Orden de visualización" 
                  style={{ width: '100%' }}
                  min={0}
                  max={999}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="Activo"
                label="Estado"
                valuePropName="checked"
              >
                <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="SerEntrada"
                label="Serie Entrada"
                rules={[
                    { max: 2, message: 'La serie de entrada debe tener máximo 2 caracteres' }
                ]}
              >
                <Select
                  showSearch
                  placeholder="Seleccione serie entrada"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                >
                  {seriesEntrada.map(serie => (
                    <Option key={serie} value={serie}>
                      {serie}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="SerSalida"
                label="Serie Salida"
                rules={[
                    { max: 2, message: 'La serie de salida debe tener máximo 2 caracteres' }
                ]}                
              >
                <Select
                  showSearch
                  placeholder="Seleccione serie salida"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                >
                  {seriesSalida.map(serie => (
                    <Option key={serie} value={serie}>
                      {serie}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Title level={5} style={{ marginBottom: 16 }}>
                <CoffeeOutlined /> Servicios Incluidos
              </Title>
            </Col>
            <Col span={8}>
              <Form.Item
                name="TieneDesayuno"
                label="Incluye Desayuno"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="TieneAlmuerzo"
                label="Incluye Almuerzo"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="TieneCena"
                label="Incluye Cena"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Title level={5} style={{ marginBottom: 16 }}>
                <ShoppingOutlined /> Servicios Adicionales
              </Title>
            </Col>
            <Col span={8}>
              <Form.Item
                name="ServicioDesayuno"
                label="Servicio Desayuno"
              >
                <InputNumber 
                  placeholder="0" 
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="ServicioAlmuerzo"
                label="Servicio Almuerzo"
              >
                <InputNumber 
                  placeholder="0" 
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="ServicioCena"
                label="Servicio Cena"
              >
                <InputNumber 
                  placeholder="0" 
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default TiposRegimen;